import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import "./arrowFeedbackModal.css";

const VW = 900;
const VH = 520;
const SW = 7;

function easeInOutCubic(t) {
  const u = Math.max(0, Math.min(1, t));
  return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
}

function buildSamples(points, extraExit = 0) {
  const pts = points.map(([x, y]) => ({ x, y }));
  if (pts.length < 2) return { samples: [{ x: 0, y: 0, d: 0 }], totalLen: 0 };
  const last = pts[pts.length - 1];
  const prev = pts[pts.length - 2];
  const dx = last.x - prev.x,
    dy = last.y - prev.y;
  const len = Math.hypot(dx, dy) || 1;
  const extended =
    extraExit > 0
      ? [...pts, { x: last.x + (dx / len) * extraExit, y: last.y + (dy / len) * extraExit }]
      : [...pts];

  const R = 10,
    STEPS = 16;
  const smooth = [];
  for (let i = 0; i < extended.length; i++) {
    if (i === 0 || i === extended.length - 1) {
      smooth.push(extended[i]);
      continue;
    }
    const a = extended[i - 1],
      b = extended[i],
      c = extended[i + 1];
    const v1x = b.x - a.x,
      v1y = b.y - a.y;
    const v2x = c.x - b.x,
      v2y = c.y - b.y;
    const l1 = Math.hypot(v1x, v1y) || 1,
      l2 = Math.hypot(v2x, v2y) || 1;
    const dot = (v1x / l1) * (v2x / l2) + (v1y / l1) * (v2y / l2);
    if (Math.abs(dot) < 0.985) {
      const rr = Math.min(R, l1 * 0.45, l2 * 0.45);
      smooth.push({
        isBez: true,
        x: b.x - (v1x / l1) * rr,
        y: b.y - (v1y / l1) * rr,
        cx: b.x,
        cy: b.y,
        ex: b.x + (v2x / l2) * rr,
        ey: b.y + (v2y / l2) * rr,
      });
    } else {
      smooth.push(b);
    }
  }

  const samples = [];
  let totalLen = 0;
  let p2 = null;
  const add = (x, y) => {
    if (p2) totalLen += Math.hypot(x - p2.x, y - p2.y);
    samples.push({ x, y, d: totalLen });
    p2 = { x, y };
  };
  for (const p of smooth) {
    if (p.isBez) {
      for (let s = 0; s <= STEPS; s++) {
        const t = s / STEPS,
          mt = 1 - t;
        add(mt * mt * p.x + 2 * mt * t * p.cx + t * t * p.ex, mt * mt * p.y + 2 * mt * t * p.cy + t * t * p.ey);
      }
    } else {
      add(p.x, p.y);
    }
  }
  return { samples, totalLen };
}

function sampleAt(samples, d) {
  if (!samples?.length) return { x: 0, y: 0 };
  if (d <= 0) return samples[0];
  const last = samples[samples.length - 1];
  if (d >= last.d) return last;
  let lo = 0,
    hi = samples.length - 1;
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1;
    if (samples[m].d <= d) lo = m;
    else hi = m;
  }
  const a = samples[lo],
    b = samples[hi];
  const t = (d - a.d) / (b.d - a.d || 1);
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function SnakePath({ points, ink, animateT }) {
  const EXIT = 100;
  const arcBody = useMemo(() => buildSamples(points, 0), [points]);
  const arcFull = useMemo(() => buildSamples(points, EXIT), [points]);
  const bodyLen = arcBody.totalLen;
  const travel = Math.max(0, arcFull.totalLen - bodyLen);
  const t = animateT ?? 0;
  const headD = bodyLen + travel * easeInOutCubic(t);
  const tailD = headD - bodyLen;
  const N = 60;
  const pts = [];
  for (let i = 0; i <= N; i++) {
    pts.push(sampleAt(arcFull.samples, Math.max(0, tailD + (headD - tailD) * (i / N))));
  }
  const dStr = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const head = pts[pts.length - 1];
  const prev2 = pts.length >= 2 ? pts[pts.length - 2] : head;
  const ang = (Math.atan2(head.y - prev2.y, head.x - prev2.x) * 180) / Math.PI;
  const AS = 17;
  const alpha = t > 0.72 ? Math.max(0, 1 - (t - 0.72) / 0.28) : 1;
  return (
    <g style={{ opacity: alpha }}>
      <path d={dStr} fill="none" stroke={ink} strokeWidth={SW} strokeLinecap="square" strokeLinejoin="miter" />
      <g transform={`translate(${head.x},${head.y}) rotate(${ang})`}>
        <polygon points={`${AS},0 ${-AS * 0.55},${-AS * 0.65} ${-AS * 0.55},${AS * 0.65}`} fill={ink} />
      </g>
    </g>
  );
}

const ARROWS = [
  {
    id: "AWESOME",
    pts: [
      [220, 420],
      [220, 290],
      [80, 290],
      [80, 130],
    ],
    label: { x: 148, y: 462, anchor: "middle" },
  },
  {
    id: "LIKED",
    pts: [
      [460, 285],
      [460, 155],
    ],
    label: { x: 390, y: 230, anchor: "middle" },
  },
  {
    id: "MINDBLOWING",
    pts: [
      [460, 370],
      [460, 450],
      [590, 450],
      [590, 130],
    ],
    label: { x: 525, y: 478, anchor: "middle" },
  },
  {
    id: "LOVED",
    pts: [
      [630, 290],
      [730, 290],
      [730, 175],
      [830, 175],
      [830, 75],
    ],
    label: { x: 745, y: 328, anchor: "middle" },
  },
];

function DotGrid() {
  const cols = 15,
    rows = 15;
  const gx = VW / cols,
    gy = VH / rows;
  const dots = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      dots.push(
        <circle
          key={`${c}-${r}`}
          cx={gx * c + gx / 2}
          cy={gy * r + gy / 2}
          r={1.6}
          fill="rgba(17,17,17,0.17)"
        />
      );
    }
  }
  return <g>{dots}</g>;
}

export default function ArrowFeedbackModal({ open, onDismiss, canSubmit = true, saving = false, onSubmit }) {
  const themeData = useSelector((s) => s.theme?.data);
  const colors = useMemo(() => {
    const t = themeData || {};
    const pick = (...keys) => {
      for (const k of keys) {
        const v = t[k];
        if (v != null && String(v).trim() !== "") return String(v).trim();
      }
      return null;
    };
    // Keep feedback arrows readable regardless of theme: default ink is black.
    const INK = "#111111";
    const LABEL = "#111111";
    const BG = pick("popup_color", "popupColor") || pick("box_bg_color", "boxBgColor") || "#eeebe5";
    const PILL_BG =
      pick("arrow_puzzle_badge_bg", "arrowPuzzleBadgeBg") ||
      pick("question_bg_color", "questionBgColor") ||
      pick("box_bg_color", "boxBgColor") ||
      "#ffd4a8";
    return { INK, LABEL, BG, PILL_BG };
  }, [themeData]);

  const [pickedId, setPickedId] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [submitted, setSubmitted] = useState(false);
  const animRef = useRef(null);
  const rafRef = useRef(null);
  const [, tick] = useState(0);

  useEffect(() => {
    if (!open) {
      setPickedId(null);
      setPhase("idle");
      setSubmitted(false);
      animRef.current = null;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, [open]);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (e.key === "Escape" && phase === "idle" && !saving) onDismiss?.();
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [open, phase, onDismiss, saving]);

  const handlePick = useCallback(
    async (id) => {
      if (!canSubmit || saving || phase !== "idle") return;
      setPhase("animating");
      setPickedId(id);
      const start = typeof performance !== "undefined" ? performance.now() : Date.now();
      const dur = 1100;
      animRef.current = { id, start, dur };
      const loop = () => {
        const a = animRef.current;
        if (!a || a.id !== id) return;
        const base = typeof performance !== "undefined" ? performance.now() : Date.now();
        const t = (base - a.start) / a.dur;
        if (t < 1) {
          tick((x) => x + 1);
          rafRef.current = requestAnimationFrame(loop);
        } else {
          tick((x) => x + 1);
          animRef.current = null;
          rafRef.current = null;
        }
      };
      rafRef.current = requestAnimationFrame(loop);

      await new Promise((r) => setTimeout(r, dur));
      setPhase("saving");
      try {
        await onSubmit?.(id);
        setSubmitted(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[ArrowFeedback] save failed:", err);
        setPickedId(null);
      } finally {
        setPhase("idle");
      }
    },
    [canSubmit, saving, phase, onSubmit]
  );

  if (!open) return null;
  const busy = phase !== "idle" || saving;

  return (
    <div
      role="presentation"
      onClick={() => !busy && onDismiss?.()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: "rgba(15,23,42,0.52)",
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="af-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(820px, 96vw)",
          borderRadius: 24,
          background: colors.BG,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.07'/%3E%3C/svg%3E\")",
          boxShadow: "0 32px 70px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.08)",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "22px 16px 6px" }}>
          <span
            id="af-title"
            style={{
              display: "inline-block",
              padding: "10px 40px",
              borderRadius: 999,
              background: colors.PILL_BG,
              color: colors.LABEL,
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            FEEDBACK
          </span>
        </div>

        {submitted ? (
          <div style={{ padding: "50px 20px 40px" }}>
            <div style={{ fontSize: 42, marginBottom: 12 }}>✓</div>
            <p
              style={{
                fontWeight: 800,
                fontSize: 15,
                color: colors.LABEL,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontFamily: "system-ui, sans-serif",
              }}
            >
              Thanks for your feedback!
            </p>
          </div>
        ) : (
          <>
            <div style={{ padding: "8px 14px 0" }}>
              <svg
                viewBox={`0 0 ${VW} ${VH}`}
                width="100%"
                height="auto"
                preserveAspectRatio="xMidYMid meet"
                style={{ display: "block", overflow: "visible", maxHeight: "56vh" }}
              >
                <DotGrid />
                {ARROWS.map((arrow) => {
                  const a = animRef.current;
                  const animateT =
                    a && a.id === arrow.id
                      ? Math.max(
                          0,
                          Math.min(1, ((typeof performance !== "undefined" ? performance.now() : Date.now()) - a.start) / a.dur)
                        )
                      : null;
                  const faded = pickedId != null && pickedId !== arrow.id;
                  return (
                    <g
                      key={arrow.id}
                      role="button"
                      tabIndex={-1}
                      onClick={() => !busy && handlePick(arrow.id)}
                      style={{
                        cursor: busy ? "default" : "pointer",
                        pointerEvents: busy ? "none" : "all",
                        opacity: faded ? 0.15 : 1,
                        transition: "opacity 180ms ease",
                        outline: "none",
                      }}
                    >
                      <SnakePath points={arrow.pts} ink={colors.INK} animateT={animateT} />
                      <text
                        x={arrow.label.x}
                        y={arrow.label.y}
                        textAnchor={arrow.label.anchor}
                        dominantBaseline="middle"
                        style={{
                          fontSize: 15,
                          fontWeight: 800,
                          letterSpacing: "0.1em",
                          fill: colors.LABEL,
                          userSelect: "none",
                          fontFamily: "system-ui, sans-serif",
                          textTransform: "uppercase",
                        }}
                      >
                        {arrow.id}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: 11,
                fontWeight: 700,
                color: colors.LABEL,
                opacity: 0.7,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                fontFamily: "system-ui, sans-serif",
              }}
            >
              Tap an arrow to submit feedback
            </p>
          </>
        )}

        <div style={{ padding: "12px 16px 20px", display: "flex", justifyContent: "center" }}>
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            style={{
              background: "transparent",
              border: `1.5px solid ${colors.INK}`,
              color: colors.INK,
              padding: "9px 30px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.35 : 1,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
